<template>
  <Dialog v-model:visible="visible" modal :draggable="false" :closable="false" style="min-width: 720px">
    <template #header>
      <h2>New In-app product (consumable/non-consumable)</h2>
    </template>
    <Form
      v-slot="$form"
      style="display: flex; flex-direction: column; gap: 16px"
      :resolver="schema"
      @submit="onFormSubmit"
    >
      <div>
        <FormField name="productId">
          <label for="name">Product ID</label>
          <InputText id="productId" fluid />
          <Message v-if="$form.productId?.invalid" severity="error" size="small" variant="simple">
            {{ $form.productId.error.message }}
          </Message>
        </FormField>
      </div>
      <FormField name="iapName">
        <label for="iapName">Name</label>
        <InputText id="iapName" fluid />
        <Message v-if="$form.iapName?.invalid" severity="error" size="small" variant="simple">
          {{ $form.iapName.error.message }}
        </Message>
      </FormField>
      <FormField name="iapDescription">
        <label for="name">Description</label>
        <Textarea id="iapDescription" auto-resize fluid />
        <Message v-if="$form.iapDescription?.invalid" severity="error" size="small" variant="simple">
          {{ $form.iapDescription.error.message }}
        </Message>
      </FormField>
      <FormField name="pricingTemplateId" style="display: flex; flex-direction: column">
        <label for="pricingTemplateId">Select price:</label>
        <Select
          input-id="pricingTemplateId"
          placeholder="Select a pricing template"
          :options="
            [...systemPricingTemplates, ...appPricingTemplates].sort(
              (a, b) => a.defaultPriceAmountMicros - b.defaultPriceAmountMicros
            )
          "
          option-label="name"
          option-value="id"
          fluid
          filter
        />
        <Message v-if="$form.pricingTemplateId?.invalid" severity="error" size="small" variant="simple">
          {{ $form.pricingTemplateId.error.message }}
        </Message>
      </FormField>
      <div :class="$style.formButtons">
        <Button type="submit" label="Create" :loading="loading" />
        <Button type="button" label="Cancel" severity="secondary" :disabled="loading" @click="onCancel()" />
      </div>
    </Form>
  </Dialog>
</template>

<script setup>
import { Button, Dialog, InputText, Message, Select, Textarea } from "primevue";
import { Form, FormField } from "@primevue/forms";
import { yupResolver } from "@primevue/forms/resolvers/yup";
import { defineEmits, defineModel, onMounted, ref } from "vue";
import { useRoute } from "vue-router";
import useIAPs from "../hooks/useIAPs";
import usePricingTemplates from "../hooks/usePricingTemplates";
import useToast from "@/hooks/useToastNotification";
import * as yup from "yup";

const { getSystemPriceTemplates, getAppPriceTemplates, systemPricingTemplates, appPricingTemplates } =
  usePricingTemplates();
const { createGooglePlayIAP } = useIAPs();
const route = useRoute();
const { toastSuccess } = useToast();
const props = defineProps({
  appId: { type: String },
});

const visible = defineModel("visible");
const emit = defineEmits(["submit"]);
const gameId = ref(route.params.gameId);
const loading = ref(false);
const schema = ref(
  yupResolver(
    yup.object({
      productId: yup
        .string()
        .required("Product ID is required")
        .matches(
          /^[a-z0-9][a-z0-9._]*$/,
          "Product ID must start with a number or lowercase letter, and can contain only numbers (0-9), lowercase letters (a-z), underscores (_), and periods (.)."
        ),
      iapName: yup.string().required("Product Name is required").max(55),
      iapDescription: yup.string().required("Product Description is required").max(200),
      pricingTemplateId: yup.string().required("Price is required"),
    })
  )
);
const onFormSubmit = async (form) => {
  if (form.valid) {
    loading.value = true;
    const result = await createGooglePlayIAP(props.appId, form.values);
    loading.value = false;
    if (result) {
      toastSuccess({
        detail: `Successfully created IAP ${form.values.iapName}!`,
        summary: "Success!",
        life: 5000,
      });
      visible.value = false;
      emit("submit");
    }
  }
};

onMounted(async () => {
  await getSystemPriceTemplates(gameId.value);
  await getAppPriceTemplates(gameId.value);
});

function onCancel() {
  visible.value = false;
}
</script>

<style module>
.formButtons {
  display: flex;
  gap: 8px;
  flex-direction: row-reverse;
}
</style>

<style scoped>
:deep(.p-message-text) {
  max-width: 660px;
}
</style>
